/**
 * The `/ensure` (idempotent provision-or-reuse) and `/start` (resume a runtime
 * the operator explicitly stopped) steps of a cloud runtime connection.
 *
 * Extracted from `connectCloudRuntimeProvisioning.ts`: these two steps run
 * before `/connect` ever gets a request, share `formatEnsureRuntimeDetail`'s
 * response shape (both read sprite identity + readiness via dual-read),
 * and share nothing with `readCloudRuntimeDescriptor` / `registerCloud-
 * RuntimeEnvironment`, which stayed behind.
 */
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";
import { buildPrimaryFetchInit } from "~/environments/primary/fetchInit";
import type { SidebarPreConnectCloudMetadata } from "./SidebarProjectItem";
import { cloudRuntimeRetryDelayMs } from "../../environments/runtime/cloudRuntimeConnectRetry";
import { describeEnsureFailure, type EnsureFailureBody } from "./ensureFailureDetail";
import { buildCmmdRuntimeEnvironmentId } from "../../environments/cmmdRuntimeEnvironmentId";
import {
  CONNECT_MAX_ATTEMPTS,
  emitConnectProgress,
  formatEnsureRuntimeDetail,
  formatRetryDelay,
  isGatewayTimeoutStatus,
  retryAfterHeader,
  shouldRetryConnectResponse,
  sleepWithAbort,
  throwCloudRuntimeConnectFailure,
  throwIfAborted,
  type CloudRuntimeConnectionOptions,
} from "./connectCloudRuntimeShared";

/**
 * `/ensure` gets the same retry budget as `/connect`. It used to have NONE — a
 * single transient 409 ("replacement is still in progress", "runtime is busy")
 * ended the whole resume before a connect was ever attempted, which is a faster
 * failure than the path it guards.
 */
const ENSURE_MAX_ATTEMPTS = CONNECT_MAX_ATTEMPTS;

export interface EnsuredCloudRuntime {
  readonly runtimeId: string;
  readonly status: string | null;
  /** Outstanding readiness work, e.g. `machine_creating`. Null once settled. */
  readonly readinessPhase: string | null;
  /**
   * The Environment's own durable identifier (FOR-32652), or null when CMMD has
   * none for this row.
   *
   * This is the value to STORE. `runtimeId` names the Sprite currently backing
   * the Environment and changes on every repair — a replacement supersedes the
   * row and inserts a successor under a fresh id — so anything keyed off it
   * restarts at zero each time the fleet is repaired.
   *
   * Null is reported rather than filled in with `cmmd-runtime:{runtimeId}`. That
   * string would look durable while being precisely the fused form
   * docs/adr/environment-outlives-sprite.md exists to separate, and it would be
   * stored as though CMMD had vouched for it.
   */
  readonly environmentId: string | null;
}

/**
 * The identifier this ensure should NAME, or undefined to name none.
 *
 * Preference order is not cosmetic. CMMD's durable identifier survives Sprite
 * replacement; the legacy form names a Sprite and is reminted on every repair.
 * A client holding both must send the one that survives, or it stays pinned to
 * the fused identity the ADR separates.
 *
 * Undefined — not null — for a project that has never been provisioned. There
 * is no Environment to name yet, and the request then stays byte-identical to
 * what every already-shipped Forge client sends.
 */
function ensureEnvironmentIdentifier(meta: SidebarPreConnectCloudMetadata): string | undefined {
  if (meta.cmmdEnvironmentId) return meta.cmmdEnvironmentId;
  return meta.runtimeId === null ? undefined : buildCmmdRuntimeEnvironmentId(meta.runtimeId);
}

function ensureSummaryFor(meta: SidebarPreConnectCloudMetadata): string {
  if (meta.runtimeId === null) return "Creating cloud project";
  if (meta.status === "destroyed") return "Provisioning cloud runtime";
  return "Ensuring cloud runtime";
}

interface EnsureAttemptOutcome {
  readonly response: Response;
  readonly errorBody: EnsureFailureBody;
}

/**
 * POST `/ensure` until it succeeds or the refusal is terminal.
 *
 * Ensure used to have NO retry at all, so one transient CMMD refusal
 * ("replacement is still in progress", "runtime is busy") ended the resume
 * before a connect was ever attempted — failing faster than the path it exists
 * to guard. It now shares connect's policy, so there is ONE answer to "may I
 * try again?" rather than two that can drift.
 *
 * Extracted rather than inlined: the loop pushed `ensureCloudRuntime` past the
 * repo's complexity gate, and a retry loop tangled with response parsing is
 * exactly the shape that hides an early return.
 */
async function postEnsureWithRetry(
  ensureUrl: string,
  meta: SidebarPreConnectCloudMetadata,
  options: CloudRuntimeConnectionOptions,
): Promise<EnsureAttemptOutcome> {
  const { signal, onProgress } = options;
  const url = resolvePrimaryEnvironmentHttpUrl(ensureUrl);
  const environmentId = ensureEnvironmentIdentifier(meta);
  const body = JSON.stringify({
    organizationId: meta.cmmdOrganizationId,
    projectId: meta.cmmdProjectId,
    githubRepoFullName: meta.githubRepoFullName,
    workspaceRoot: meta.workspaceRoot,
    // FOR-32652. Names the Environment this ensure means, in whichever form
    // this client holds. Spread so it is OMITTED rather than sent as null for a
    // project with no Environment yet.
    //
    // FOR-2405's `taskId` used to be spread in here too. FOR-32649 retired
    // per-task machine isolation outright, and CMMD's ensure schema is
    // non-strict, so this client could have kept sending a dead field for a
    // whole release with nothing anywhere reporting it.
    ...(environmentId === undefined ? {} : { environmentId }),
  });

  // Two independent counters. `attempt` is the bounded app-layer budget
  // (`ENSURE_MAX_ATTEMPTS`) and only a genuine CMMD refusal may spend it;
  // `gatewayAttempt` paces the unbounded gateway-timeout backoff and never
  // touches that budget. CodeRabbit (PR #3581): a `for` loop's own counter
  // advances on every `continue`, so sharing one between the two meant a 524
  // ahead of real refusals silently stole a slot from the bounded budget, and
  // a project that genuinely needed all 8 real chances could be declared
  // failed on its 7th.
  let attempt = 1;
  let gatewayAttempt = 1;
  for (;;) {
    throwIfAborted(signal);
    const response = await fetch(
      url,
      buildPrimaryFetchInit({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal,
      }),
    );
    if (response.ok) return { response, errorBody: {} };

    // FOR-3579: Cloudflare's own 524/502/504 page is HTML, not JSON. `.json()`
    // must fall back rather than let a parse error masquerade as the real
    // failure.
    const errorBody = (await response
      .json()
      .catch(() => ({}))) as EnsureAttemptOutcome["errorBody"];

    // A gateway timeout means CMMD's own handler is still running server-side
    // — the seed it kicked off inside this request outlived the edge's patience,
    // not CMMD's own patience. That is "still provisioning," never a refusal,
    // so it skips the fixed attempt budget entirely: only the caller's own
    // abort (the flight's overall deadline) is allowed to end it.
    if (isGatewayTimeoutStatus(response.status)) {
      const delayMs = cloudRuntimeRetryDelayMs({
        attempt: gatewayAttempt,
        retryAfter: retryAfterHeader(response),
      });
      emitConnectProgress(onProgress, {
        stage: "runtime-ensure-retry",
        summary: "Cloud project is still provisioning",
        detail: `Gateway timed out (${response.status}) while CMMD finished setting up the workspace; checking again in ${formatRetryDelay(delayMs)}.`,
        attempt: gatewayAttempt,
      });
      await sleepWithAbort(delayMs, signal);
      gatewayAttempt += 1;
      continue;
    }

    if (!shouldRetryConnectResponse(response, errorBody, attempt, ENSURE_MAX_ATTEMPTS)) {
      return { response, errorBody };
    }

    const delayMs = cloudRuntimeRetryDelayMs({
      attempt,
      retryAfter: retryAfterHeader(response),
    });
    emitConnectProgress(onProgress, {
      stage: "runtime-ensure-retry",
      summary: "Cloud runtime is still settling",
      detail: `CMMD returned ${response.status}; retrying in ${formatRetryDelay(delayMs)}.`,
      attempt,
      maxAttempts: ENSURE_MAX_ATTEMPTS,
    });
    await sleepWithAbort(delayMs, signal);
    attempt += 1;
  }
}

/**
 * Provision-or-reuse. Returns the runtime id CMMD settled on.
 *
 * Idempotent by contract: `forge_runtimes` is unique on (organization, user)
 * for live project Environments (`forge_runtimes_org_user_environment_idx`,
 * CMMD #4437, 2026-08-28; see docs/adr/one-environment-per-seat.md), so the
 * second and later calls return the SAME runtime rather than creating another
 * sprite, whichever project asked. This is what makes "provision on first
 * thread" safe to call on EVERY thread.
 *
 * FOR-32652 added the Environment identifier to the request, and it did NOT
 * replace that guarantee. The identifier selects an Environment; the owner
 * tuple is still the provisioning slot and still the only guard that survives
 * two CMMD processes racing. That stays true until a Seat can own a second
 * Environment (CMMD-32653), at which point the identifier becomes required.
 */
export async function ensureCloudRuntime(
  meta: SidebarPreConnectCloudMetadata,
  options: CloudRuntimeConnectionOptions,
): Promise<EnsuredCloudRuntime> {
  // CMMD forge-context owns idempotent provisioning and clone readiness even
  // when the sidebar's cached runtime status still says `running`.
  const { signal, onProgress } = options;
  throwIfAborted(signal);
  const ensureUrl = "/api/cmmd/forge-runtimes/ensure";
  emitConnectProgress(onProgress, {
    stage: "runtime-ensure-request",
    summary: ensureSummaryFor(meta),
    detail: ensureUrl,
  });
  const { response: ensureRes, errorBody: ensureErrBody } = await postEnsureWithRetry(
    ensureUrl,
    meta,
    options,
  );
  if (!ensureRes.ok) {
    emitConnectProgress(onProgress, {
      stage: "runtime-ensure-failed",
      summary: "Cloud runtime provisioning failed",
      detail: describeEnsureFailure(ensureRes.status, ensureErrBody),
    });
    throwCloudRuntimeConnectFailure(ensureRes, ensureErrBody, { stage: "runtime-ensure-failed" });
  }
  const ensureBody = (await ensureRes.json()) as {
    runtime?: {
      runtimeId?: string | null;
      environmentId?: string | null;
      spriteName?: string | null;
      spriteId?: string | null;
      flyMachineId?: string | null;
      flyVolumeId?: string | null;
      readinessPhase?: string | null;
      status?: string | null;
    };
  };
  emitConnectProgress(onProgress, {
    stage: "runtime-ensured",
    summary: "Cloud runtime ensured",
    detail: formatEnsureRuntimeDetail(ensureBody.runtime ?? {}),
  });
  throwIfAborted(signal);

  // The ensure response is the sole authority for the runtime identity. A
  // cached catalog id can point at a removed or superseded row; falling back to
  // it turns a malformed/partial ensure response into an unintended connect
  // request for stale state.
  const runtimeId = ensureBody.runtime?.runtimeId;
  if (!runtimeId) {
    throw new Error("CMMD did not return a runtime identity after provisioning.");
  }
  return {
    runtimeId,
    status: typeof ensureBody.runtime?.status === "string" ? ensureBody.runtime.status : null,
    readinessPhase:
      typeof ensureBody.runtime?.readinessPhase === "string"
        ? ensureBody.runtime.readinessPhase
        : null,
    // Narrowed to a string rather than passed through, so a CMMD that has not
    // deployed the field yet yields null instead of `undefined` leaking into a
    // stored environment record as a missing key.
    environmentId:
      typeof ensureBody.runtime?.environmentId === "string"
        ? ensureBody.runtime.environmentId
        : null,
  };
}

type StoppedRuntimeStartDisposition = "connect" | "start";

function stoppedRuntimeStartDisposition(status: string | null): StoppedRuntimeStartDisposition {
  switch (status) {
    case "running":
    case "starting":
    case "provisioning":
      return "connect";
    case "stopped":
    case "suspended":
      return "start";
    default:
      throw new Error("CMMD did not confirm the stopped project state before starting it.");
  }
}

interface RuntimeStartResponse {
  readonly runtimeId?: string | null;
  readonly status?: string | null;
  readonly spriteName?: string | null;
  readonly spriteId?: string | null;
  readonly flyMachineId?: string | null;
  readonly readinessPhase?: string | null;
}

function assertRuntimeStartResponse(
  runtime: RuntimeStartResponse | undefined,
  runtimeId: string,
): asserts runtime is RuntimeStartResponse & {
  readonly runtimeId: string;
  readonly status: "running" | "starting";
} {
  if (
    runtime?.runtimeId !== runtimeId ||
    (runtime.status !== "starting" && runtime.status !== "running")
  ) {
    throw new Error("CMMD did not confirm the requested project start.");
  }
}

function runtimeStartFailureDetail(body: { readonly error?: unknown }, status: number): string {
  return typeof body.error === "string" ? body.error : `CMMD returned ${status}.`;
}

export async function startStoppedCloudRuntime(
  ensured: EnsuredCloudRuntime,
  meta: SidebarPreConnectCloudMetadata,
  options: CloudRuntimeConnectionOptions,
): Promise<void> {
  // Gate on the OWNER'S INTENT, not on the cached display status.
  //
  // `stopMachine` on Sprites is `suspendSprite`, so a stopped runtime's sprite
  // goes warm exactly like an idle one and the reconciler adopts that as
  // `status: "suspended"`. Keying this on `status === "stopped"` therefore
  // skipped the start for the very runtime that needed it, and the connect that
  // followed took CMMD's 409 "Forge runtime is stopped. Start it before
  // connecting." — the client and the server reading two different fields to
  // answer one question. `desiredState` is the field CMMD's gate reads, and the
  // only one that survives the reconcile.
  if (meta.desiredState !== "stopped" && meta.status !== "stopped") return;
  if (meta.runtimeId !== ensured.runtimeId) {
    throw new Error("CMMD returned a different runtime identity while starting the project.");
  }
  if (stoppedRuntimeStartDisposition(ensured.status) === "connect") return;

  const runtimeId = ensured.runtimeId;
  const { signal, onProgress } = options;
  throwIfAborted(signal);
  const startUrl = `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/start`;
  emitConnectProgress(onProgress, {
    stage: "runtime-start-request",
    summary: "Starting cloud project",
    detail: startUrl,
  });
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl(startUrl),
    buildPrimaryFetchInit({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organizationId: meta.cmmdOrganizationId }),
      signal,
    }),
  );
  const body = (await response.json().catch(() => ({}))) as {
    readonly runtime?: RuntimeStartResponse;
    readonly error?: unknown;
  };
  if (!response.ok) {
    emitConnectProgress(onProgress, {
      stage: "runtime-start-failed",
      summary: "Cloud runtime start failed",
      detail: runtimeStartFailureDetail(body, response.status),
    });
    throwCloudRuntimeConnectFailure(response, body, { stage: "runtime-start-failed" });
  }
  assertRuntimeStartResponse(body.runtime, runtimeId);
  emitConnectProgress(onProgress, {
    stage: "runtime-started",
    summary: "Cloud project start requested",
    detail: formatEnsureRuntimeDetail(body.runtime),
  });
  throwIfAborted(signal);
}
